import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { createApiUrlWithPrefix } from '../config/api';

const SettingsContext = createContext();

export function useSettings() {
  return useContext(SettingsContext);
}

// Helper to apply club branding to the browser
function applyBranding(brandingMap) {
  if (brandingMap['club.name']) {
    document.title = brandingMap['club.name'];
  }
  const logoOrFavicon = brandingMap['club.logo.url'] || brandingMap['club.favicon.url'];
  if (logoOrFavicon && logoOrFavicon.trim()) {
    let link = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.getElementsByTagName('head')[0].appendChild(link);
    }
    link.href = logoOrFavicon.trim();
  }

  // Dynamic Theme Colors (Generates primary shades dynamically)
  const primaryColor = brandingMap['theme.primary.color'] || '#059669';
  if (primaryColor) {
    document.documentElement.style.setProperty('--color-primary-600', primaryColor);
    document.documentElement.style.setProperty('--color-primary-500', primaryColor);
    document.documentElement.style.setProperty('--color-primary-700', primaryColor);
    document.documentElement.style.setProperty('--color-primary-50', primaryColor + '15'); // 8% opacity tint
    document.documentElement.style.setProperty('--color-primary-100', primaryColor + '30'); // 18% opacity tint
  }

  // Dynamic Secondary & Accent Theme Colors
  const secondaryColor = brandingMap['theme.secondary.color'] || '#3b82f6';
  if (secondaryColor) {
    document.documentElement.style.setProperty('--color-secondary-600', secondaryColor);
    document.documentElement.style.setProperty('--color-secondary-500', secondaryColor);
    document.documentElement.style.setProperty('--color-secondary-50', secondaryColor + '15');
  }
}

export function SettingsProvider({ children }) {
  const [settings, setSettings] = useState({});
  const [branding, setBranding] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      // Load all admin settings (pricing etc.)
      const res = await axios.get(createApiUrlWithPrefix('admin/settings'));
      const map = {};
      const brandingMap = {};
      res.data.forEach(s => {
        map[s.key] = s.value;
        if (s.group === 'Club Branding') {
          brandingMap[s.key] = s.value || '';
        }
      });
      setSettings(map);
      if (Object.keys(brandingMap).length > 0) {
        setBranding(prev => ({ ...prev, ...brandingMap }));
        applyBranding(brandingMap);
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load settings:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBranding = useCallback(async () => {
    try {
      // Public endpoint – no auth required
      const res = await axios.get(createApiUrlWithPrefix('admin/settings/public?group=Club Branding'));
      setBranding(res.data || {});
      applyBranding(res.data || {});
    } catch (err) {
      console.error('Failed to load club branding:', err);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchBranding();
  }, [fetchSettings, fetchBranding]);

  const getSetting = useCallback((key, defaultValue = null) => {
    return settings[key] !== undefined ? settings[key] : defaultValue;
  }, [settings]);

  const getSettingAsNumber = useCallback((key, defaultValue = 0) => {
    const val = settings[key];
    if (val === undefined || val === null) return defaultValue;
    const num = parseFloat(val);
    return isNaN(num) ? defaultValue : num;
  }, [settings]);

  const getBranding = useCallback((key, defaultValue = '') => {
    const val = branding[key] || settings[key];
    return (val !== undefined && val !== null && val.trim() !== '') ? val : defaultValue;
  }, [branding, settings]);

  return (
    <SettingsContext.Provider value={{
      settings,
      branding,
      loading,
      error,
      getSetting,
      getSettingAsNumber,
      getBranding,
      refresh: fetchSettings,
      refreshBranding: fetchBranding
    }}>
      {children}
    </SettingsContext.Provider>
  );
}
